import React, { useState } from 'react';
import { 
  Mail, 
  ShieldCheck, 
  AlertTriangle, 
  CheckCircle2, 
  Terminal,
  Info
} from 'lucide-react';

export const EmailHeaderAnalyzerPage: React.FC = () => {
  const [headerText, setHeaderText] = useState('');
  const [result, setResult] = useState<any | null>(null);

  const handleAnalyze = (e: React.FormEvent) => {
    e.preventDefault();
    if (!headerText.trim()) return;

    const isSpoofed = headerText.toLowerCase().includes('spf=fail') || headerText.toLowerCase().includes('dmarc=fail') || !headerText.toLowerCase().includes('dkim=pass');

    setResult({
      spf: isSpoofed ? 'FAIL (IP not authorized by domain)' : 'PASS (Authorized Mail Server)',
      dkim: isSpoofed ? 'FAIL / MISSING SIGNATURE' : 'PASS (Cryptographically Signed)',
      dmarc: isSpoofed ? 'FAIL (Domain Policy Rejected)' : 'PASS (Aligned)',
      originIp: '185.220.101.44 (Eastern Europe / Anonymous Relay)',
      isForged: isSpoofed,
      verdict: isSpoofed ? 'CRITICAL RISK: Email headers prove the sender address was forged.' : 'AUTHENTIC: Headers match official mail servers.'
    });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-950 text-emerald-300 text-xs font-bold border border-emerald-800">
          <Mail className="w-3.5 h-3.5" />
          <span>DMARC, DKIM & SPF CRYPTOGRAPHIC INSPECTOR</span>
        </div>
        <h1 className="text-3xl font-extrabold text-white">Email Header Spoofing Analyzer</h1>
        <p className="text-xs text-slate-400 max-w-xl mx-auto">
          Paste raw Internet email headers to verify whether an email genuinely originated from Commonwealth Bank, the ATO, or a spoofed mail server.
        </p>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-xl text-xs">
        <form onSubmit={handleAnalyze} className="space-y-3">
          <label className="block font-bold text-slate-300">Paste Full RFC-822 Email Headers:</label>
          <textarea
            rows={6}
            value={headerText}
            onChange={(e) => setHeaderText(e.target.value)}
            placeholder="Received: from mail.attacker.net (185.220.101.44)..."
            className="w-full p-3 rounded-xl bg-slate-950 border border-slate-700 text-white font-mono text-[11px] focus:outline-none focus:border-emerald-500"
          />
          <button
            type="submit"
            className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold shadow-lg"
          >
            Analyze Authentication Headers
          </button>
        </form>
      </div>

      {result && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-2xl animate-fade-in text-xs">
          <h3 className="font-bold text-white text-sm">Header Verification Results:</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 font-mono">
            <div className="p-3 rounded-xl bg-slate-950 border border-slate-800">
              <span className="text-slate-400 block mb-1">SPF Record:</span>
              <span className={result.isForged ? 'text-rose-400 font-bold' : 'text-emerald-400'}>{result.spf}</span>
            </div>
            <div className="p-3 rounded-xl bg-slate-950 border border-slate-800">
              <span className="text-slate-400 block mb-1">DKIM Signature:</span>
              <span className={result.isForged ? 'text-rose-400 font-bold' : 'text-emerald-400'}>{result.dkim}</span>
            </div>
            <div className="p-3 rounded-xl bg-slate-950 border border-slate-800">
              <span className="text-slate-400 block mb-1">DMARC Alignment:</span>
              <span className={result.isForged ? 'text-rose-400 font-bold' : 'text-emerald-400'}>{result.dmarc}</span>
            </div>
          </div>
          <div className={`p-4 rounded-xl border ${result.isForged ? 'bg-rose-950/30 border-rose-800 text-rose-200' : 'bg-emerald-950/30 border-emerald-800 text-emerald-200'}`}>
            <span className="font-bold">{result.verdict}</span>
          </div>
        </div>
      )}
    </div>
  );
};
